import React, { useState, useEffect } from 'react';
import { formatNumberWithCommas, parseFormattedNumber, convertToPersianWords } from '../../utils/numberFormatter';

interface FormattedNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  showWords?: boolean;
  min?: number;
  step?: number;
  required?: boolean;
  disabled?: boolean;
}

const FormattedNumberInput: React.FC<FormattedNumberInputProps> = ({
  value,
  onChange,
  placeholder = "مبلغ را وارد کنید",
  className = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500",
  showWords = true,
  min,
  step,
  required = false,
  disabled = false
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [persianWords, setPersianWords] = useState('');

  // Update display value when prop value changes
  useEffect(() => {
    if (value) {
      const formatted = formatNumberWithCommas(value);
      setDisplayValue(formatted);
      
      if (showWords) {
        const words = convertToPersianWords(value);
        setPersianWords(words);
      }
    } else {
      setDisplayValue('');
      setPersianWords('');
    }
  }, [value, showWords]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow only numbers and commas
    const cleanValue = inputValue.replace(/[^0-9,]/g, '');
    
    // Parse the formatted number back to clean number
    const cleanNumber = parseFormattedNumber(cleanValue);
    
    // Update the parent component with clean number
    onChange(cleanNumber);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
    if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  return (
    <div className="space-y-1">
      <input
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        min={min}
        step={step}
        required={required}
        disabled={disabled}
        dir="ltr"
      />
      {showWords && persianWords && (
        <div className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border">
          <span className="font-medium">به حروف:</span> {persianWords}
        </div>
      )}
    </div>
  );
};

export default FormattedNumberInput;
